import React from "react";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { Table, PageHeader, Card } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";

import { TableId, useFrontendTable } from "components/table";
import DataFormat from "components/utils/DataFormat";
import Helmet from "components/helmet/Helmet";

import { Language, usePermission } from "imports/Utils";

import AccountTypeSelect from "./components/selects/AccountTypeSelect";

import { ChildPropsFromArray } from "types/commons";

import { AccountType } from "__generated__/enums";
import { GetListAllAccountsDocument, GetListAllAccountsQuery } from "__generated__/view/users/graphql/user.queries.generated";

type UserFromQuery = ChildPropsFromArray<GetListAllAccountsQuery, "listAllAccounts">

const Users = () => {

	const navigate = useNavigate();
	const { isReadOnly, isCompanyUser, isAdmin } = usePermission();
	const session = useStoreState(store => store.session);

	const { data, loading } = useQuery(GetListAllAccountsDocument);

	const { tableProps } = useFrontendTable({
		id: TableId.Users,
		content: data?.listAllAccounts ?? [],
		onCustomFilter: (property: string) => {
			if (property === "accountType") {
				return (field: AccountType, value: AccountType[]) => value.includes(field);
			}
		},
	});

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("USERS_TITLE")}
					<Helmet title={intl.formatMessage({ id: "USERS_TITLE" })} />
				</PageHeader.Title>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={loading}
					onRowDoubleClick={row => {
						if (row.id == session.user?.properties.id || isAdmin) {
							navigate(`/view/users/${row.id}/details`);
						}
					}}
					columns={[ {
						id: "accountType",
						title: intl.formatMessage({ id: "USER_TYPE" }),
						render: (row: UserFromQuery) => `${DataFormat.userType(row.accountType)}`,
						sortable: true,
						sortKey: "accountType",
						width: 200,
						filterComponent: (value, onChange) => (
							<AccountTypeSelect
								label={intl.formatMessage({ id: "USER_TYPE" })}
								value={value}
								onChange={onChange}
								multiple />
						),
					}, {
						id: "username",
						title: intl.formatMessage({ id: "USER_USERNAME" }),
						sortable: true,
						width: 150,
						filterComponent: "TEXT",
					}, {
						id: "email",
						title: intl.formatMessage({ id: "USER_EMAIL" }),
						sortable: true,
						width: 150,
						filterComponent: "TEXT",
					}, {
						id: "relation.name",
						title: intl.formatMessage({ id: "USER_RELATION" }),
						sortable: true,
						width: 150,
						filterComponent: "TEXT",
					} ] }
					rowActions={isReadOnly ? undefined : [ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/users/${row.id}/edit`),
						disabled: (row) => (isCompanyUser || isAdmin) ? false : row.id != session.user?.properties.id,
						hidden: isReadOnly,
					} ]} />
			</Card>
		</React.Fragment>
	);
};

export default Users;
